/**
 * useHomeSLDemoCrmStageChartLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait HomeSLDemoCrmStageChart
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, HomeSLDemoCrmStageData, HomeSLDemoCrmStageChartEventPayloadMap } from '@app/shared';
import { useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import type { BusEvent } from '@almadar/core';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type HomeSLDemoCrmStageChartState =
  | 'idle';

export type HomeSLDemoCrmStageChartEvent =
  | 'INIT'
  | 'ITEMS_LOADED'
  | 'BoardItemsLoaded';

type HomeSLDemoCrmStageChartSlot = 'main';
interface HomeSLDemoCrmStageChartInternalState {
  machineState: HomeSLDemoCrmStageChartState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeSLDemoCrmStageChartEvent | null;
  slotWriter: Partial<Record<HomeSLDemoCrmStageChartSlot, HomeSLDemoCrmStageChartEvent | null>>;
  loading: boolean;
  pendingEvent: HomeSLDemoCrmStageChartEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeSLDemoCrmStageData>;
}

type HomeSLDemoCrmStageChartAction =
  | { type: 'EVENT_SUCCESS'; event: HomeSLDemoCrmStageChartEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: HomeSLDemoCrmStageChartSlot[]; renderClear?: HomeSLDemoCrmStageChartSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: HomeSLDemoCrmStageChartEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof HomeSLDemoCrmStageData & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function homeSLDemoCrmStageChartReducer(
  state: HomeSLDemoCrmStageChartInternalState,
  action: HomeSLDemoCrmStageChartAction
): HomeSLDemoCrmStageChartInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as HomeSLDemoCrmStageChartState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<HomeSLDemoCrmStageData>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<HomeSLDemoCrmStageData>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<HomeSLDemoCrmStageData>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: HomeSLDemoCrmStageChartInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface HomeSLDemoCrmStageChartConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  aggregation: 'count' | 'sum' | 'avg' | 'min' | 'max';
  categoryField: string;
  categoryLabels: { lead: string[]; proposal: string[]; qualified: string[]; won: string[] };
  chartType: 'bar' | 'line' | 'pie' | 'area' | 'donut' | 'histogram' | 'scatter';
  drillEvent: string;
  filterField: string;
  filterValue: string;
  height: number;
  limit: number;
  period: 'day' | 'week' | 'month' | 'quarter' | 'year';
  series: { color?: string; dashed?: boolean; data: { label: string; value: number }[]; name: string }[];
  showLegend: boolean;
  showValues: boolean;
  sortDir: 'asc' | 'desc';
  sortField: 'value' | 'label' | 'none';
  stack: 'none' | 'stack' | 'normalize';
  subtitle?: string;
  timeAxis: boolean;
  timeField: string;
  title?: string;
  valueField: string;
  viewPattern: string;
}

const DEFAULT_HOME_SL_DEMO_CRM_STAGE_CHART_CONFIG = {
  aggregation: "count",
  categoryField: "stage",
  categoryLabels: { lead: ["i18n/t", "std-almadar-demo-crm-page:stage.lead"], proposal: ["i18n/t", "std-almadar-demo-crm-page:stage.proposal"], qualified: ["i18n/t", "std-almadar-demo-crm-page:stage.qualified"], won: ["i18n/t", "std-almadar-demo-crm-page:stage.won"] },
  chartType: "donut",
  drillEvent: "",
  filterField: "",
  filterValue: "",
  height: 200,
  limit: 0,
  period: "month",
  series: [],
  showLegend: true,
  showValues: false,
  sortDir: "desc",
  sortField: "none",
  stack: "none",
  timeAxis: false,
  timeField: "date",
  valueField: "",
  viewPattern: "chart",
} as HomeSLDemoCrmStageChartConfig;

export interface HomeSLDemoCrmStageChartLogicReturn {
  state: HomeSLDemoCrmStageChartState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeSLDemoCrmStageChartEvent | null;
  slotWriter: Partial<Record<HomeSLDemoCrmStageChartSlot, HomeSLDemoCrmStageChartEvent | null>>;
  loading: boolean;
  pendingEvent: HomeSLDemoCrmStageChartEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeSLDemoCrmStageData>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeSLDemoCrmStageChartEventPayloadMap]: (payload?: HomeSLDemoCrmStageChartEventPayloadMap[K]) => Promise<EventResponse> };
  config: HomeSLDemoCrmStageChartConfig;
}

const __CASCADE_EVENTS = new Set<string>(['BoardItemsLoaded', 'ITEMS_LOADED']);

export function useHomeSLDemoCrmStageChartLogic(config?: Partial<HomeSLDemoCrmStageChartConfig>): HomeSLDemoCrmStageChartLogicReturn {
  const mergedConfig: HomeSLDemoCrmStageChartConfig = { ...DEFAULT_HOME_SL_DEMO_CRM_STAGE_CHART_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/home-sl-demo-crm-stage-chart', orbitalName: 'HomeSL', traitName: 'HomeSLDemoCrmStageChart' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(homeSLDemoCrmStageChartReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const cascadeReceivedRef = useRef<TraitStateSnapshot['cascadeReceived']>([]);

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['HomeSLDemoCrmStageData'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'ITEMS_LOADED') {
      // idle → idle (on ITEMS_LOADED)
        {

        const rows = payloadObj?.data;

        _fields['chartData'] = (Object.entries((((((rows ?? []) as readonly EntityRow[]).filter((row) => getNestedValue(row, "stage")) ?? []) as readonly EntityRow[]).reduce((acc: Record<string, EntityRow[]>, item: EntityRow) => { const __k = String((item as EntityRow)?.["stage"] ?? ''); (acc[__k] ??= []).push(item); return acc; }, {})) ?? {})).map(((entry) => ({ label: getNestedValue(entry, "0"), value: ((getNestedValue(entry, "1") ?? []) as { readonly length: number }).length })));
        fields = { ...(fields ?? {}), 'chartData': _fields['chartData'] } as Record<string, EventPayloadValue | undefined>;
        }

        _fields['chartData'] = fields?.chartData;
        fields = { ...(fields ?? {}), 'chartData': _fields['chartData'] } as Record<string, EventPayloadValue | undefined>;
        _fields['chartData'] = fields?.chartData;
        fields = { ...(fields ?? {}), 'chartData': _fields['chartData'] } as Record<string, EventPayloadValue | undefined>;
        _fields['chartData'] = ((0 > 0) ? ((fields?.chartData as readonly EventPayloadValue[] | undefined) ?? []).slice(0, 0) : fields?.chartData);
        fields = { ...(fields ?? {}), 'chartData': _fields['chartData'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeSLDemoCrmStageChartSlot[]; clear: HomeSLDemoCrmStageChartSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as HomeSLDemoCrmStageChartSlot[], clear: [] as HomeSLDemoCrmStageChartSlot[] };
          case 'ITEMS_LOADED': return { write: ['main'] as HomeSLDemoCrmStageChartSlot[], clear: [] as HomeSLDemoCrmStageChartSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'HomeSLDemoCrmStageData': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as HomeSLDemoCrmStageChartEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    if (__CASCADE_EVENTS.has(event)) {
      cascadeReceivedRef.current.push({ event, ...(payload !== undefined ? { payload } : {}), timestamp: Date.now() });
    }
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'HomeSLDemoCrmStageChart': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeSLDemoCrmStageChartEventPayloadMap]: (payload?: HomeSLDemoCrmStageChartEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'ITEMS_LOADED': (payload?: HomeSLDemoCrmStageChartEventPayloadMap['ITEMS_LOADED']) => dispatch('ITEMS_LOADED', payload),
  };

  useUIEvents(enqueueEvent, 'HomeSL.HomeSLDemoCrmStageChart', ['INIT', 'ITEMS_LOADED'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('HomeSLDemoCrmStageChart', (): TraitStateSnapshot => ({
      traitName: 'HomeSLDemoCrmStageChart',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT', 'ITEMS_LOADED'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: cascadeReceivedRef.current,
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'BoardItemsLoaded' (from HomeSLDemoCrmBoard) → trigger 'ITEMS_LOADED'
    const unsub_boarditemsloaded_from_homesldemocrmboard_to_items_loaded = eventBus.on('UI:HomeSL.HomeSLDemoCrmBoard.BoardItemsLoaded', (event: BusEvent) => {
      if (!event.source || !(event.source as { fromBridge?: boolean }).fromBridge) {
        return;
      }
      if (!claimDelivery(event.source, 'HomeSL.HomeSLDemoCrmStageChart', 'ITEMS_LOADED')) return;
      enqueueEvent('ITEMS_LOADED', event.payload);
    });
    return () => {
      if (typeof unsub_boarditemsloaded_from_homesldemocrmboard_to_items_loaded === 'function') unsub_boarditemsloaded_from_homesldemocrmboard_to_items_loaded();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  mergedConfig.subtitle = mergedConfig.subtitle ?? (__t("std-almadar-demo-crm-page:chart.stagesSub"));
  mergedConfig.title = mergedConfig.title ?? (__t("std-almadar-demo-crm-page:chart.stages"));
  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
